import { createElement, useState, type ReactNode } from 'react';
import { Icon } from '@/kit/Icon';
import { Tooltip } from '@/kit/Tooltip';

export interface TabProps {
  label: ReactNode;
  children?: ReactNode;
  /** A short one-line description of this tab, shown as a hover tooltip on its info icon while it is active. */
  infoText?: string;
}

/** A tab of a `TabGroup` (Angular `<mat-tab label>`); it renders nothing on its own. */
export function Tab(props: TabProps): null {
  void props;
  return null;
}

interface TabGroupProps {
  /** Angular `[selectedIndex]` (controlled). Leave undefined for uncontrolled. */
  selectedIndex?: number;
  onSelectedIndexChange?: (index: number) => void;
  /** Angular `mat-align-tabs`. */
  alignTabs?: 'start' | 'center' | 'end';
  className?: string;
  tabs: TabProps[];
}

/**
 * `<mat-tab-group>`: MDC DOM and class names. Like Material, only the active tab's content exists in the DOM
 * (content of other tabs is destroyed when you leave them).
 */
export function TabGroup({
  selectedIndex,
  onSelectedIndexChange,
  alignTabs = 'start',
  className = '',
  tabs,
}: TabGroupProps) {
  const [internal, setInternal] = useState(0);
  const active = Math.min(selectedIndex ?? internal, Math.max(tabs.length - 1, 0));

  const select = (index: number) => {
    // Material only emits selectedIndexChange when the tab actually changes.
    if (index === active) return;
    if (selectedIndex === undefined) setInternal(index);
    onSelectedIndexChange?.(index);
  };

  const pagination = (side: 'before' | 'after') => (
    <button
      aria-hidden="true"
      type="button"
      tabIndex={-1}
      className={`mat-ripple mat-mdc-tab-header-pagination mat-mdc-tab-header-pagination-${side} mat-mdc-tab-header-pagination-disabled`}
      disabled
    >
      <div className="mat-mdc-tab-header-pagination-chevron"></div>
    </button>
  );

  // Same DOM as MatTabGroup (header with pagination stubs, labelled tabs, one body per tab of which only the active
  // one has content), so Material's own tab styles apply unchanged.
  return createElement(
    'mat-tab-group',
    {
      'mat-align-tabs': alignTabs,
      className: `mat-mdc-tab-group mat-primary mat-mdc-tab-group-stretch-tabs ${className}`.trim(),
    },
    createElement(
      'mat-tab-header',
      { className: 'mat-mdc-tab-header' },
      pagination('before'),
      <div className="mat-mdc-tab-label-container">
        <div className="mat-mdc-tab-list" role="tablist">
          <div className="mat-mdc-tab-labels">
            {tabs.map((tab, index) => (
              <div
                key={index}
                className={`mdc-tab mat-mdc-tab mat-mdc-focus-indicator${index === active ? ' mdc-tab--active mdc-tab-indicator--active' : ''}`}
                role="tab"
                aria-posinset={index + 1}
                aria-setsize={tabs.length}
                aria-selected={index === active}
                aria-disabled="false"
                tabIndex={index === active ? 0 : -1}
                onClick={() => select(index)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') select(index);
                }}
              >
                <span className="mdc-tab__ripple"></span>
                <div className="mat-ripple mat-mdc-tab-ripple"></div>
                <span className="mdc-tab__content">
                  <span className="mdc-tab__text-label">{tab.label}</span>
                  {index === active && tab.infoText && (
                    <Tooltip title={tab.infoText}>
                      <Icon name="info" outlined className="tab-info-icon" />
                    </Tooltip>
                  )}
                </span>
                <span className="mdc-tab-indicator">
                  <span className="mdc-tab-indicator__content mdc-tab-indicator__content--underline"></span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>,
      pagination('after'),
    ),
    <div className="mat-mdc-tab-body-wrapper">
      {tabs.map((tab, index) =>
        createElement(
          'mat-tab-body',
          {
            key: index,
            role: 'tabpanel',
            className: `mat-mdc-tab-body${index === active ? ' mat-mdc-tab-body-active' : ''}`,
            'aria-hidden': index !== active,
          },
          <div
            className="mat-mdc-tab-body-content"
            style={
              index === active
                ? { transform: 'none' }
                : {
                    transform: `translate3d(${index < active ? '-' : ''}100%, 0px, 0px)`,
                    minHeight: '1px',
                    visibility: 'hidden',
                  }
            }
          >
            {index === active ? tab.children : null}
          </div>,
        ),
      )}
    </div>,
  );
}
